import { useState, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Upload, FileText, Loader2, AlertCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { reportError } from '@/lib/error-reporting';

interface ParsedResume {
  skills: string[];
  work_history: {
    company: string;
    title: string;
    start_date: string | null;
    end_date: string | null;
    description: string | null;
    skills_used: string[];
    is_current: boolean;
  }[];
  education: {
    institution: string;
    degree: string | null;
    field_of_study: string | null;
    start_date: string | null;
    end_date: string | null;
    description: string | null;
  }[];
  certifications: {
    name: string;
    issuing_organization: string | null;
    issue_date: string | null;
    expiration_date: string | null;
    credential_id: string | null;
  }[];
}

interface ResumeUploadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onParsed: (data: ParsedResume) => void;
}

export const ResumeUploadDialog = ({ open, onOpenChange, onParsed }: ResumeUploadDialogProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate
    if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
      setError('Only PDF files are supported.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('File too large. Maximum size is 5MB.');
      return;
    }

    setFileName(file.name);
    setError(null);
    setUploading(true);

    let isValidationError = false;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/parse-resume`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
          body: formData,
        },
      );

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        if (errData.retry_after_seconds) {
          const mins = Math.ceil(errData.retry_after_seconds / 60);
          throw new Error(`Too many uploads. Please try again in ${mins} minute${mins > 1 ? 's' : ''}.`);
        }
        isValidationError = response.status >= 400 && response.status < 500;
        throw new Error(errData.error || `Upload failed (${response.status})`);
      }

      const { data } = await response.json();
      onParsed(data);
      onOpenChange(false);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to parse resume';
      setError(msg);
      if (!isValidationError) {
        reportError(err, 'settings', { action: 'resume-upload' });
      }
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && fileInputRef.current) {
      const dt = new DataTransfer();
      dt.items.add(file);
      fileInputRef.current.files = dt.files;
      fileInputRef.current.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Import from Resume</DialogTitle>
          <DialogDescription>
            Upload a PDF resume to auto-extract your work history, education, skills, and certifications.
          </DialogDescription>
        </DialogHeader>

        <div
          className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 transition-colors"
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleDrop}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,application/pdf"
            className="hidden"
            onChange={handleFileSelect}
          />

          {uploading ? (
            <div className="space-y-3">
              <Loader2 className="w-10 h-10 mx-auto text-primary animate-spin" />
              <div>
                <p className="text-sm font-medium">Processing {fileName}...</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Extracting text and analyzing with AI
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="w-12 h-12 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
                {fileName ? (
                  <FileText className="w-6 h-6 text-primary" />
                ) : (
                  <Upload className="w-6 h-6 text-primary" />
                )}
              </div>
              <div>
                <p className="text-sm font-medium">
                  {fileName || 'Drop your resume here or click to browse'}
                </p>
                <p className="text-xs text-muted-foreground mt-1">PDF only, max 5MB</p>
              </div>
            </div>
          )}
        </div>

        {error && (
          <div className="flex items-start gap-2 p-3 rounded-md bg-destructive/10 text-destructive text-sm">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <p className="text-xs text-muted-foreground">
          Your resume is processed in-memory and never stored. Only the extracted data is saved after your review.
        </p>
      </DialogContent>
    </Dialog>
  );
};
